// TanStack-Query-Hooks für die Verwaltungs-API.
import { useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { ApiError, api, getAll } from './client';
import type {
  Assignment, Dashboard, Device, DeviceDetail, DeviceGroup, LayoutRow, Location, Me, Media, MediaDetail, MediaVersion, Playlist, PlaylistDetail,
  PlaylistRevision, Schedule, ScheduleDetail, TemplateCatalog, TenantListItem, TenantUsage, TenantUser,
} from './types';

export const qk = {
  me: ['me'] as const,
  setup: ['setup'] as const,
  dashboard: ['dashboard'] as const,
  devices: ['devices'] as const,
  device: (id: string) => ['devices', id] as const,
  groups: ['device-groups'] as const,
  locations: ['locations'] as const,
  media: ['media'] as const,
  mediaOne: (id: string) => ['media', id] as const,
  layouts: ['layouts'] as const,
  layout: (id: string) => ['layouts', id] as const,
  templateCatalog: ['template-catalog'] as const,
  playlists: ['playlists'] as const,
  playlist: (id: string) => ['playlists', id] as const,
  revisions: (id: string) => ['playlists', id, 'revisions'] as const,
  schedules: ['schedules'] as const,
  schedule: (id: string) => ['schedules', id] as const,
  assignments: ['assignments'] as const,
  users: ['users'] as const,
  tenants: ['tenants'] as const,
  usage: ['tenant-usage'] as const,
};

/** Angemeldeter Benutzer; null wenn nicht angemeldet. */
export function useMe() {
  return useQuery({
    queryKey: qk.me,
    queryFn: async () => {
      try {
        return await api.get<Me>('/auth/me');
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 60_000,
  });
}

export const useSetupStatus = () => useQuery({ queryKey: qk.setup, queryFn: () => api.get<{ needsSetup: boolean }>('/setup/status'), retry: false });

const tenantScoped = (me: Me | null | undefined) => !!me?.activeTenantId;

export function useTenantQuery<T>(key: QueryKey, fn: () => Promise<T>, opts: { refetchInterval?: number | ((data: T | undefined) => number | false); enabled?: boolean } = {}) {
  const { data: me } = useMe();
  const ri = opts.refetchInterval;
  return useQuery({
    queryKey: [...key, me?.activeTenantId ?? 'none'],
    queryFn: fn,
    enabled: tenantScoped(me) && (opts.enabled ?? true),
    ...(ri ? { refetchInterval: typeof ri === 'function' ? (q: { state: { data: T | undefined } }) => ri(q.state.data) : ri } : {}),
  });
}

export const useDashboard = () => useTenantQuery(qk.dashboard, () => api.get<Dashboard>('/dashboard'), { refetchInterval: 20_000 });
// TODO WebSocket /ws/manager (4.4): bis dahin Live-Status per Abfrage alle 15 s
export const useDevices = () => useTenantQuery(qk.devices, () => getAll<Device>('/devices'), { refetchInterval: 15_000 });
export const useDevice = (id: string) => useTenantQuery(qk.device(id), () => api.get<DeviceDetail>(`/devices/${id}`), { refetchInterval: 15_000 });
export const useGroups = () => useTenantQuery(qk.groups, async () => (await api.get<{ items: DeviceGroup[] }>('/device-groups')).items);
export const useLocations = () => useTenantQuery(qk.locations, () => getAll<Location>('/locations'));
// Medien in Prüfung (Worker): alle 3 s nachfragen, bis alle bereit/abgelehnt sind (bis MediaStatusChanged über /ws/manager kommt)
const PENDING_MEDIA = new Set(['uploading', 'quarantined', 'scanning', 'processing']);
const pendingInterval = (versions: (MediaVersion | null | undefined)[]) => (versions.some((v) => v && PENDING_MEDIA.has(v.status)) ? 3000 : false);
export const useMedia = () => useTenantQuery(qk.media, () => getAll<Media>('/media'), { refetchInterval: (d) => pendingInterval((d ?? []).map((m) => m.current)) });
export const useMediaOne = (id: string) => useTenantQuery(qk.mediaOne(id), () => api.get<MediaDetail>(`/media/${id}`), { refetchInterval: (d) => pendingInterval(d?.versions ?? []) });
export const useLayouts = (opts: { enabled?: boolean } = {}) => useTenantQuery(qk.layouts, async () => (await api.get<{ items: LayoutRow[] }>('/layouts')).items, opts);
// Katalog ändert sich nur mit neuer Server-Version
export const useTemplateCatalog = () => useTenantQuery(qk.templateCatalog, () => api.get<TemplateCatalog>('/templates/catalog'));
export const useLayout = (id: string) => useTenantQuery(qk.layout(id), () => api.get<LayoutRow>(`/layouts/${id}`));
export const usePlaylists = () => useTenantQuery(qk.playlists, () => getAll<Playlist>('/playlists'));
export const usePlaylist = (id: string) => useTenantQuery(qk.playlist(id), () => api.get<PlaylistDetail>(`/playlists/${id}`));
export const useRevisions = (id: string) => useTenantQuery(qk.revisions(id), async () => (await api.get<{ items: PlaylistRevision[] }>(`/playlists/${id}/revisions`)).items);
export const useSchedules = () => useTenantQuery(qk.schedules, async () => (await api.get<{ items: Schedule[] }>('/schedules')).items);
export const useSchedule = (id: string) => useTenantQuery(qk.schedule(id), () => api.get<ScheduleDetail>(`/schedules/${id}`));
export const useAssignments = () => useTenantQuery(qk.assignments, async () => (await api.get<{ items: Assignment[] }>('/assignments')).items);
export const useUsers = () => useTenantQuery(qk.users, async () => (await api.get<{ items: TenantUser[] }>('/users')).items);
export const useUsage = () => useTenantQuery(qk.usage, () => api.get<TenantUsage>('/tenant/usage'));

export function useTenants(enabled = true) {
  const { data: me } = useMe();
  return useQuery({ queryKey: qk.tenants, queryFn: () => getAll<TenantListItem>('/tenants'), enabled: enabled && !!me?.user.isPlatformAdmin });
}

/** Nach Mandantenwechsel alle mandantenbezogenen Daten verwerfen. */
export function useResetTenantData() {
  const qc = useQueryClient();
  return (me: Me) => {
    qc.setQueryData(qk.me, me);
    qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' && q.queryKey[0] !== 'tenants' });
  };
}

export const can = (me: Me | null | undefined, p: string) => !!me?.permissions.includes(p);
